import { gql } from "@apollo/client";
import { CHARTS_MAX_DOCUMENTS } from "./constants";

export const FETCH_LAST_SIGNED_PUBLIC_ADDRESS = gql`
  query($orderBy: String!, $orderDirection: String!) {
    confirmTransferEvents(orderBy: $orderBy, orderDirection: $orderDirection) {
      signature
      oldSignerAddress
      newSignerAddress
      epoch
      dynasty
      timestamp
      transactionHash
    }
  }
`;

export const FETCH_CURRENT_EPOCH = gql`
  query {
    epoches {
      epoch
    }
  }
`;

export const FETCH_CURRENT_DYNASTY = gql`
  query {
    dynasties {
      dynasty
    }
  }
`;

export const FETCH_CURRENT_MODE = gql`
  query {
    modes {
      mode
    }
  }
`;

export const FETCH_LAST_FINALIZED_BLOCK = gql`
  query($orderBy: String!, $orderDirection: String!) {
    finalizeBlockEvents(orderBy: $orderBy, orderDirection: $orderDirection) {
      blockWinner
      signature
      sender
      signerAddress
      epoch
      dynasty
      messageData
      requests {
        id
        requestId
        indexedAt
        requestFinalizedEpoch
        status
        resultAggregationType
        resultRequestingChainDomain
        resultDestinationChainDomain
        requestFirstCommittedEpoch
        resultTarget
        resultValues
      }
      requestIds
      numRequests
      transactionHash
    }
  }
`;

export const FETCH_FINALIZED_BLOCKS = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
  ) {
    finalizeBlockEvents(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
    ) {
      blockWinner
      signature
      requests {
        id
        requestId
        indexedAt
        requestFinalizedEpoch
        status
        resultAggregationType
        resultRequestingChainDomain
        resultDestinationChainDomain
        requestFirstCommittedEpoch
        resultTarget
        resultValues
      }
      requestIds
      numRequests
      sender
      signerAddress
      epoch
      dynasty
      messageData
      transactionHash
      timestamp
    }
  }
`;

export const FETCH_REQUESTS = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
    $requestId: String
    $requestingChainDomain: [Int]
    $status: [String]
    $destinationChainDomain: [Int]
  ) {
    requests(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: {
        requestId_contains: $requestId
        resultRequestingChainDomain_in: $requestingChainDomain
        status_in: $status
        resultDestinationChainDomain_in: $destinationChainDomain
      }
    ) {
      id
      requestId
      numCommits
      indexedAt
      requestFinalizedEpoch
      requestFirstCommittedEpoch
      resultDestinationChainDomain
      resultRequestingChainDomain
      resultTarget
      resultValues
      status
      epochConsensusReached
    }
  }
`;

export const FETCH_REQUESTS_BY_STATUS = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
    $status: [String]
  ) {
    requests(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: { status_in: $status }
    ) {
      id
      requestId
      numCommits
      indexedAt
      requestFinalizedEpoch
      requestFirstCommittedEpoch
      resultDestinationChainDomain
      resultRequestingChainDomain
      resultTarget
      resultValues
      status
      epochConsensusReached
    }
  }
`;

export const FETCH_REQUESTS_BY_STATUS_AND_REQUEST_ID = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
    $status: [String]
    $requestId: String
  ) {
    requests(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: { status_in: $status, requestId: $requestId }
    ) {
      id
      requestId
      numCommits
      indexedAt
      requestFinalizedEpoch
      requestFirstCommittedEpoch
      resultDestinationChainDomain
      resultRequestingChainDomain
      resultTarget
      resultValues
      status
      epochConsensusReached
    }
  }
`;

export const FETCH_REQUEST = gql`
  query($requestId: String!) {
    requests(where: { requestId: $requestId }) {
      id
      requestId
      numCommits
      indexedAt
      requestFinalizedEpoch
      requestFirstCommittedEpoch
      resultDestinationChainDomain
      resultRequestingChainDomain
      resultTarget
      resultValues
      status
      epochConsensusReached
    }
  }
`;

export const FETCH_COMMITTED_EVENTS_BY_REQUEST_ID = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
    $requestId: String!
  ) {
    requestCommittedEvents(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: { requestId: $requestId }
    ) {
      id
      epoch
      requestId
      sender
      timestamp
      transactionHash
      operatorId
      commitment
      dynasty
    }
  }
`;

export const FETCH_REVEALED_EVENTS_BY_REQUEST_ID = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
    $requestId: String!
  ) {
    requestRevealedEvents(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: { requestId: $requestId }
    ) {
      id
      requestId
      epoch
      sender
      requestingChainDomain
      target
      timestamp
      transactionHash
      values
      destinationChainDomain
      dynasty
    }
  }
`;

export const FETCH_ATTESTED_SIGNERS = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
  ) {
    attestedEvents(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
    ) {
      epoch
      dynasty
      operatorId
      status
      sender
      signerAddress
      votes
      timestampWithVotes
      transactionHash
      timestamp
    }
  }
`;

export const FETCH_OPERATORS = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
  ) {
    operators(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
    ) {
      stake
      operatorId
      apy
      status
      lastActiveTimestamp
      lastAttestedEpoch
      lastAttestedDynasty
      lastFinalizedBlock
      lastEpochChurnedOut
      lastDynastyChurnedOut
      isChurnedOut
      address
      delegationAccepted
      dToken
    }
  }
`;

export const FETCH_OPERATOR_BY_ADDRESS = gql`
  query($address: String!) {
    operators(where: { address: $address }) {
      stake
      operatorId
      commission
      delegationAccepted
      apy
      status
      lastActiveTimestamp
      lastAttestedEpoch
      lastAttestedDynasty
      lastFinalizedBlock
      lastEpochChurnedOut
      lastDynastyChurnedOut
      isChurnedOut
      address
      delegationAccepted
      dToken
      claimableOperatorRewards
    }
  }
`;

export const FETCH_ALL_TRANSACTIONS = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
  ) {
    transactions(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
    ) {
      id
      timestamp
      transactionHash
      address
      dynasty
      epoch
      action
      attestedEvent {
        epoch
        dynasty
        operatorId
        sender
        status
        signerAddress
        transactionHash
      }
      confirmTransferEvent {
        id
        signature
        oldSignerAddress
        newSignerAddress
        epoch
        dynasty
        timestamp
        transactionHash
      }
      requestCommittedEvent {
        id
        epoch
        dynasty
        requestId
        sender
        timestamp
        transactionHash
        operatorId
        commitment
      }
      requestStatusChangedEvent {
        id
        requestId
        epoch
        dynasty
        status
        timestamp
        transactionHash
      }
      requestRevealedEvent {
        id
        epoch
        dynasty
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        timestamp
        transactionHash
        values
      }
      operatorSnitchedEvent {
        id
        requestId
        destinationChainDomain
        bountyHunter
        dynasty
        epoch
        requestingChainDomain
        target
        timestamp
        transactionHash
        operatorId
        values
      }
      createRequestEvent {
        dynasty
        requesterAddress
        numRequests
        timestamp
        epoch
        transactionHash
        sourceChainId
        sourceAddress
        payload
        targetChainId
      }
      finalizeBlockEvent {
        blockWinner
        signature
        sender
        signerAddress
        epoch
        dynasty
        messageData
        requests {
          id
          requestId
          indexedAt
          requestFinalizedEpoch
          status
          resultAggregationType
          resultRequestingChainDomain
          resultDestinationChainDomain
          requestFirstCommittedEpoch
          resultTarget
          resultValues
        }
        requestIds
        numRequests
        transactionHash
      }
      operatorSelectionEvent {
        operatorId
        activeSet
        iteration
        transactionHash
        timestamp
        isOperatorSelected
      }
      stakedEvent {
        amount
        dynasty
        epoch
        transactionHash
        operator
        operatorId
      }

      validatorsProvedEvent {
        id
        operatorId
        operatorStake
        timestamp
        transactionHash
        epoch
        dynasty
      }

      tooManyOperatorsSnitchedEvent {
        id
        activeSet
        dynasty
        epoch
        numOperatorsSuspended
        timestamp
        transactionHash
      }

      operatorsChurnedOutEvent {
        churnedOutOperators
        dynasty
        epoch
        id
        timestamp
        transactionHash
      }
      WithdrawalRequestedEvent {
        id
        epoch
        dynasty
        amount
        operator
        operatorId
        timestamp
        transactionHash
        unlockTime
      }
      WithdrawalCompletedEvent {
        id
        operator
        operatorId
        timestamp
        epoch
        transactionHash
        dynasty
        amount
      }
      withdrawEvent {
        operatorId
        operator
        transactionHash
        mode
        id
        epoch
        dynasty
      }
      parameterEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      parameterChangeScheduledEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      attestBlameEvent {
        blameType
        culprits
        dynasty
        epoch
        id
        sender
        transactionHash
      }
      slashedEvent {
        dynasty
        epoch
        id
        newStake
        prevStake
        sender
        operator
        operatorId
        transactionHash
      }
      stakeChangeEvent {
        dynasty
        epoch
        id
        newStake
        prevStake
        reason
        timestamp
        transactionHash
        operatorId
      }
    delegatorPoolChangeEvent {
      id
      operatorId
      prevDelegatorPool
      newDelegatorPool
      reason
      epoch
      dynasty
      transactionHash
      timestamp
  }

      delegatedEvent {
        id
        delegator
        operator
        operatorId
        amount
        prevTotalStake
        newTotalStake
        totalSupply
        epoch
        dynasty
        transactionHash
        timestamp
      }

      quitNetworkEvent {
        id
        operator
        operatorId
        toBeChurnedOutForUnstake
        epoch
        dynasty
        transactionHash
        timestamp
      }

      withdrawSignalledEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        transactionHash
        timestamp
      }

      withdrawInitiatedEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        newStake
        transactionHash
        timestamp
      }

      withdrawExecutedEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        newStake
        transactionHash
        timestamp
      }

      resetSignalLockEvent {
        id
        operatorId
        operator
        delegator
        epoch
        dynasty
        transactionHash
        timestamp
      }

      operatorRewardChanged {
        id
        operator
        reason
        prevClaimableAmount
        newClaimableAmount
        dynasty
        epoch
        transactionHash
      }

      metalayerRecoveryEvent {
        id
        activeSetBeingJailedSlashed
        epoch
        dynasty
        transactionHash
      }

      blameConsensusEvent {
        id
        blameType
        culprits
        epoch
        dynasty
        transactionHash
      }

      chainStatusChangedEvent {
        id
        chainDomain
        isSupported
        epoch
        dynasty
        sender
        timestamp
        transactionHash
      }
      signerProofDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
        transactionHash
      }

      validatorWithdrawalCredentialVerifiedEvent {
        id
        operatorId
        validatorIndex
        epoch
        dynasty
        transactionHash
        timestamp
        sender
      }
      operatorJailedEvent {
        jailEndDynasty
        sender
        transactionHash
        operatorId
        epoch
        dynasty
        id
      }
      blockDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
      }
      commissionUpdatedEvent {
        commission
        id
        operator
        operatorId
      }
      delegationAcceptanceChangedEvent {
        acceptDelegation
        id
        operator
        operatorId
      }
      escapeHatchEvent {
        amount
        dynasty
        epoch
        id
        sender
      }
      genesisResetTimestampSetEvent {
        genesisResetTimestamp
        id
        initModeTimePeriod
      }
      messageConsensusReachedEvent {
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        dynasty
        epoch
        id
        message
      }
      signerConsensusReachedEvent {
        signerAddress
        id
        epoch
        dynasty
      }
    }
  }
`;

export const FETCH_OPERATOR_DAILY_STATS = gql`
  query($operatorId: String!, $timestamp_lt: Int, $timestamp_gt: Int) {
    operatorStats(
      where: { 
        operatorId: $operatorId,
        timestamp_lt: $timestamp_lt,
        timestamp_gt: $timestamp_gt
      },
      orderBy: timestamp,
      orderDirection: asc,
      first: ${CHARTS_MAX_DOCUMENTS}
    ) {
      id
      operatorId
      dynasty
      stake
      numCommits
      numReveals
      timestamp
    }
  }
`;

export const FETCH_OPERATOR_WEEKLY_STATS = gql`
  query($operatorId: String!, $timestamp_lt: Int, $timestamp_gt: Int) {
    operatorStats(
      where: { 
        operatorId: $operatorId,
        timestamp_lt: $timestamp_lt,
        timestamp_gt: $timestamp_gt,
        week: true,
      },
      orderBy: timestamp,
      orderDirection: asc,
      first: ${CHARTS_MAX_DOCUMENTS}
    ) {
      id
      operatorId
      dynasty
      stake
      numCommits
      numReveals
      timestamp
    }
  }
`;

export const FETCH_OPERATOR_MONTHLY_STATS = gql`
  query($operatorId: String!, $timestamp_lt: Int, $timestamp_gt: Int) {
    operatorStats(
      where: { 
        operatorId: $operatorId,
        timestamp_lt: $timestamp_lt,
        timestamp_gt: $timestamp_gt,
        month: true
      },
      orderBy: timestamp,
      orderDirection: asc,
      first: ${CHARTS_MAX_DOCUMENTS}
    ) {
      id
      operatorId
      dynasty
      stake
      numCommits
      numReveals
      timestamp
    }
  }
`;

export const FETCH_BLOCKS_STATS = gql`
  query($timestamp_lt: Int, $timestamp_gt: Int, $week: Boolean, $month: Boolean) {
    finalizeBlockEvents(
      where: {
        timestamp_lt: $timestamp_lt,
        timestamp_gt: $timestamp_gt,
        week: $week,
        month: $month
      }
      orderBy: timestamp
      orderDirection: desc
      first: ${CHARTS_MAX_DOCUMENTS}
    ) {
      dynasty
      numRequests
      timestamp
      epoch
    }
  }
`;

export const FETCH_TRANSACTION_BY_HASH = gql`
  query($hash: String!) {
    transactions(where: { transactionHash: $hash }) {
      id
      transactionHash
      epoch
      dynasty
      timestamp
      address
      action
      attestedEvent {
        epoch
        dynasty
        operatorId
        sender
        status
        signerAddress
        transactionHash
      }
      confirmTransferEvent {
        id
        signature
        oldSignerAddress
        newSignerAddress
        epoch
        dynasty
        timestamp
        transactionHash
      }
      requestCommittedEvent {
        id
        epoch
        dynasty
        requestId
        sender
        timestamp
        transactionHash
        operatorId
        commitment
      }
      requestStatusChangedEvent {
        id
        requestId
        epoch
        dynasty
        status
        timestamp
        transactionHash
      }
      requestRevealedEvent {
        id
        epoch
        dynasty
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        timestamp
        transactionHash
        values
      }
      operatorSnitchedEvent {
        id
        requestId
        destinationChainDomain
        bountyHunter
        dynasty
        epoch
        requestingChainDomain
        target
        timestamp
        transactionHash
        operatorId
        values
      }
      createRequestEvent {
        dynasty
        requesterAddress
        numRequests
        timestamp
        epoch
        transactionHash
        sourceChainId
        sourceAddress
        payload
        targetChainId
      }
      finalizeBlockEvent {
        blockWinner
        signature
        sender
        signerAddress
        epoch
        dynasty
        messageData
        requests {
          id
          requestId
          indexedAt
          requestFinalizedEpoch
          status
          resultAggregationType
          resultRequestingChainDomain
          resultDestinationChainDomain
          requestFirstCommittedEpoch
          resultTarget
          resultValues
        }
        requestIds
        numRequests
        transactionHash
      }
      operatorSelectionEvent {
        operatorId
        activeSet
        iteration
        transactionHash
        timestamp
        isOperatorSelected
      }
      stakedEvent {
        amount
        dynasty
        epoch
        transactionHash
        operator
        operatorId
      }

      validatorsProvedEvent {
        id
        operatorId
        operatorStake
        timestamp
        transactionHash
        epoch
        dynasty
      }

      tooManyOperatorsSnitchedEvent {
        id
        activeSet
        dynasty
        epoch
        numOperatorsSuspended
        timestamp
        transactionHash
      }

      operatorsChurnedOutEvent {
        churnedOutOperators
        dynasty
        epoch
        id
        timestamp
        transactionHash
      }
      WithdrawalRequestedEvent {
        id
        epoch
        dynasty
        amount
        operator
        operatorId
        timestamp
        transactionHash
        unlockTime
      }
      WithdrawalCompletedEvent {
        id
        operator
        operatorId
        timestamp
        epoch
        transactionHash
        dynasty
        amount
      }
      withdrawEvent {
        operatorId
        operator
        transactionHash
        mode
        id
        epoch
        dynasty
      }
      parameterEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      parameterChangeScheduledEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      attestBlameEvent {
        blameType
        culprits
        dynasty
        epoch
        id
        sender
        transactionHash
      }
      slashedEvent {
        dynasty
        epoch
        id
        newStake
        prevStake
        sender
        operator
        operatorId
        transactionHash
      }
      stakeChangeEvent {
        dynasty
        epoch
        id
        newStake
        prevStake
        reason
        timestamp
        transactionHash
        operatorId
      }
    delegatorPoolChangeEvent {
      id
      operatorId
      prevDelegatorPool
      newDelegatorPool
      reason
      epoch
      dynasty
      transactionHash
      timestamp
  }

      delegatedEvent {
        id
        delegator
        operator
        operatorId
        amount
        prevTotalStake
        newTotalStake
        totalSupply
        epoch
        dynasty
        transactionHash
        timestamp
      }

      quitNetworkEvent {
        id
        operator
        operatorId
        toBeChurnedOutForUnstake
        epoch
        dynasty
        transactionHash
        timestamp
      }

      withdrawSignalledEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        transactionHash
        timestamp
      }

      withdrawInitiatedEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        newStake
        transactionHash
        timestamp
      }

      withdrawExecutedEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        newStake
        transactionHash
        timestamp
      }

      resetSignalLockEvent {
        id
        operatorId
        operator
        delegator
        epoch
        dynasty
        transactionHash
        timestamp
      }
      operatorRewardChanged {
        id
        operator
        reason
        prevClaimableAmount
        newClaimableAmount
        dynasty
        epoch
        transactionHash
      }

      metalayerRecoveryEvent {
        id
        activeSetBeingJailedSlashed
        epoch
        dynasty
        transactionHash
      }

      blameConsensusEvent {
        id
        blameType
        culprits
        epoch
        dynasty
        transactionHash
      }

      chainStatusChangedEvent {
        id
        chainDomain
        isSupported
        epoch
        dynasty
        sender
        timestamp
        transactionHash
      }
      signerProofDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
        transactionHash
      }

      validatorWithdrawalCredentialVerifiedEvent {
        id
        operatorId
        validatorIndex
        epoch
        dynasty
        transactionHash
        timestamp
        sender
      }
      operatorJailedEvent {
        jailEndDynasty
        sender
        transactionHash
        operatorId
        epoch
        dynasty
        id
      }
      blockDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
      }
      commissionUpdatedEvent {
        commission
        id
        operator
        operatorId
      }
      delegationAcceptanceChangedEvent {
        acceptDelegation
        id
        operator
        operatorId
      }
      escapeHatchEvent {
        amount
        dynasty
        epoch
        id
        sender
      }
      genesisResetTimestampSetEvent {
        genesisResetTimestamp
        id
        initModeTimePeriod
      }
      messageConsensusReachedEvent {
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        dynasty
        epoch
        id
        message
      }
      signerConsensusReachedEvent {
        signerAddress
        id
        epoch
        dynasty
      }
    }
  }
`;

export const FETCH_TRANSACTION_BY_ADDRESS = gql`
  query(
    $address: String!
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
  ) {
    transactions(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: { address: $address }
    ) {
      id
      timestamp
      transactionHash
      address
      dynasty
      epoch
      action
      attestedEvent {
        epoch
        dynasty
        operatorId
        sender
        status
        signerAddress
        transactionHash
      }
      confirmTransferEvent {
        id
        signature
        oldSignerAddress
        newSignerAddress
        epoch
        dynasty
        timestamp
        transactionHash
      }
      requestCommittedEvent {
        id
        epoch
        dynasty
        requestId
        sender
        timestamp
        transactionHash
        operatorId
        commitment
      }
      requestStatusChangedEvent {
        id
        requestId
        epoch
        dynasty
        status
        timestamp
        transactionHash
      }
      requestRevealedEvent {
        id
        epoch
        dynasty
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        timestamp
        transactionHash
        values
      }
      operatorSnitchedEvent {
        id
        requestId
        destinationChainDomain
        bountyHunter
        dynasty
        epoch
        requestingChainDomain
        target
        timestamp
        transactionHash
        operatorId
        values
      }
      createRequestEvent {
        dynasty
        requesterAddress
        numRequests
        timestamp
        epoch
        transactionHash
        sourceChainId
        sourceAddress
        payload
        targetChainId
      }
      finalizeBlockEvent {
        blockWinner
        signature
        sender
        signerAddress
        epoch
        dynasty
        messageData
        requests {
          id
          requestId
          indexedAt
          requestFinalizedEpoch
          status
          resultAggregationType
          resultRequestingChainDomain
          resultDestinationChainDomain
          requestFirstCommittedEpoch
          resultTarget
          resultValues
        }
        requestIds
        numRequests
        transactionHash
      }
      operatorSelectionEvent {
        operatorId
        activeSet
        iteration
        transactionHash
        timestamp
        isOperatorSelected
      }
      stakedEvent {
        amount
        dynasty
        epoch
        transactionHash
        operator
        operatorId
      }

      validatorsProvedEvent {
        id
        operatorId
        operatorStake
        timestamp
        transactionHash
        epoch
        dynasty
      }

      tooManyOperatorsSnitchedEvent {
        id
        activeSet
        dynasty
        epoch
        numOperatorsSuspended
        timestamp
        transactionHash
      }

      operatorsChurnedOutEvent {
        churnedOutOperators
        dynasty
        epoch
        id
        timestamp
        transactionHash
      }
      WithdrawalRequestedEvent {
        id
        epoch
        dynasty
        amount
        operator
        operatorId
        timestamp
        transactionHash
        unlockTime
      }
      WithdrawalCompletedEvent {
        id
        operator
        operatorId
        timestamp
        epoch
        transactionHash
        dynasty
        amount
      }
      withdrawEvent {
        operatorId
        operator
        transactionHash
        mode
        id
        epoch
        dynasty
      }
      parameterEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      parameterChangeScheduledEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      attestBlameEvent {
        blameType
        culprits
        dynasty
        epoch
        id
        sender
        transactionHash
      }
      slashedEvent {
        dynasty
        epoch
        id
        newStake
        prevStake
        sender
        operator
        operatorId
        transactionHash
      }
      stakeChangeEvent {
        dynasty
        epoch
        id
        newStake
        prevStake
        reason
        timestamp
        transactionHash
        operatorId
      }
    delegatorPoolChangeEvent {
      id
      operatorId
      prevDelegatorPool
      newDelegatorPool
      reason
      epoch
      dynasty
      transactionHash
      timestamp
  }

      delegatedEvent {
        id
        delegator
        operator
        operatorId
        amount
        prevTotalStake
        newTotalStake
        totalSupply
        epoch
        dynasty
        transactionHash
        timestamp
      }

      quitNetworkEvent {
        id
        operator
        operatorId
        toBeChurnedOutForUnstake
        epoch
        dynasty
        transactionHash
        timestamp
      }

      withdrawSignalledEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        transactionHash
        timestamp
      }

      withdrawInitiatedEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        newStake
        transactionHash
        timestamp
      }

      withdrawExecutedEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        newStake
        transactionHash
        timestamp
      }

      resetSignalLockEvent {
        id
        operatorId
        operator
        delegator
        epoch
        dynasty
        transactionHash
        timestamp
      }

      operatorRewardChanged {
        id
        operator
        reason
        prevClaimableAmount
        newClaimableAmount
        dynasty
        epoch
        transactionHash
      }

      metalayerRecoveryEvent {
        id
        activeSetBeingJailedSlashed
        epoch
        dynasty
        transactionHash
      }

      blameConsensusEvent {
        id
        blameType
        culprits
        epoch
        dynasty
        transactionHash
      }

      chainStatusChangedEvent {
        id
        chainDomain
        isSupported
        epoch
        dynasty
        sender
        timestamp
        transactionHash
      }
      signerProofDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
        transactionHash
      }

      validatorWithdrawalCredentialVerifiedEvent {
        id
        operatorId
        validatorIndex
        epoch
        dynasty
        transactionHash
        timestamp
        sender
      }
      operatorJailedEvent {
        jailEndDynasty
        sender
        transactionHash
        operatorId
        epoch
        dynasty
        id
      }
      blockDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
      }
      commissionUpdatedEvent {
        commission
        id
        operator
        operatorId
      }
      delegationAcceptanceChangedEvent {
        acceptDelegation
        id
        operator
        operatorId
      }
      escapeHatchEvent {
        amount
        dynasty
        epoch
        id
        sender
      }
      genesisResetTimestampSetEvent {
        genesisResetTimestamp
        id
        initModeTimePeriod
      }
      messageConsensusReachedEvent {
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        dynasty
        epoch
        id
        message
      }
      signerConsensusReachedEvent {
        signerAddress
        id
        epoch
        dynasty
      }
    }
  }
`;

export const FETCH_TRANSACTION_BY_ADDRESS_ACTION = gql`
  query(
    $address: String!
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
    $actions: [String]
  ) {
    transactions(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: { address: $address, action_in: $actions }
    ) {
      id
      timestamp
      transactionHash
      address
      dynasty
      epoch
      action
      attestedEvent {
        epoch
        dynasty
        operatorId
        sender
        status
        signerAddress
        transactionHash
      }
      confirmTransferEvent {
        id
        signature
        oldSignerAddress
        newSignerAddress
        epoch
        dynasty
        timestamp
        transactionHash
      }
      requestCommittedEvent {
        id
        epoch
        dynasty
        requestId
        sender
        timestamp
        transactionHash
        operatorId
        commitment
      }
      requestStatusChangedEvent {
        id
        requestId
        epoch
        dynasty
        status
        timestamp
        transactionHash
      }
      requestRevealedEvent {
        id
        epoch
        dynasty
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        timestamp
        transactionHash
        values
      }
      operatorSnitchedEvent {
        id
        requestId
        destinationChainDomain
        bountyHunter
        dynasty
        epoch
        requestingChainDomain
        target
        timestamp
        transactionHash
        operatorId
        values
      }
      createRequestEvent {
        dynasty
        requesterAddress
        numRequests
        timestamp
        epoch
        transactionHash
        sourceChainId
        sourceAddress
        payload
        targetChainId
      }
      finalizeBlockEvent {
        blockWinner
        signature
        sender
        signerAddress
        epoch
        dynasty
        messageData
        requests {
          id
          requestId
          indexedAt
          requestFinalizedEpoch
          status
          resultAggregationType
          resultRequestingChainDomain
          resultDestinationChainDomain
          requestFirstCommittedEpoch
          resultTarget
          resultValues
        }
        requestIds
        numRequests
        transactionHash
      }
      operatorSelectionEvent {
        operatorId
        activeSet
        iteration
        transactionHash
        timestamp
        isOperatorSelected
      }
      stakedEvent {
        amount
        dynasty
        epoch
        transactionHash
        operator
        operatorId
      }

      validatorsProvedEvent {
        id
        operatorId
        operatorStake
        timestamp
        transactionHash
        epoch
        dynasty
      }

      tooManyOperatorsSnitchedEvent {
        id
        activeSet
        dynasty
        epoch
        numOperatorsSuspended
        timestamp
        transactionHash
      }

      operatorsChurnedOutEvent {
        churnedOutOperators
        dynasty
        epoch
        id
        timestamp
        transactionHash
      }
      WithdrawalRequestedEvent {
        id
        epoch
        dynasty
        amount
        operator
        operatorId
        timestamp
        transactionHash
        unlockTime
      }
      WithdrawalCompletedEvent {
        id
        operator
        operatorId
        timestamp
        epoch
        transactionHash
        dynasty
        amount
      }
      withdrawEvent {
        operatorId
        operator
        transactionHash
        mode
        id
        epoch
        dynasty
      }
      parameterEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      parameterChangeScheduledEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      attestBlameEvent {
        blameType
        culprits
        dynasty
        epoch
        id
        sender
        transactionHash
      }
      slashedEvent {
        dynasty
        epoch
        id
        newStake
        prevStake
        sender
        operator
        operatorId
        transactionHash
      }
      stakeChangeEvent {
        dynasty
        epoch
        id
        newStake
        prevStake
        reason
        timestamp
        transactionHash
        operatorId
      }
    delegatorPoolChangeEvent {
      id
      operatorId
      prevDelegatorPool
      newDelegatorPool
      reason
      epoch
      dynasty
      transactionHash
      timestamp
  }

      delegatedEvent {
        id
        delegator
        operator
        operatorId
        amount
        prevTotalStake
        newTotalStake
        totalSupply
        epoch
        dynasty
        transactionHash
        timestamp
      }

      quitNetworkEvent {
        id
        operator
        operatorId
        toBeChurnedOutForUnstake
        epoch
        dynasty
        transactionHash
        timestamp
      }

      withdrawSignalledEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        transactionHash
        timestamp
      }

      withdrawInitiatedEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        newStake
        transactionHash
        timestamp
      }

      withdrawExecutedEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        newStake
        transactionHash
        timestamp
      }

      resetSignalLockEvent {
        id
        operatorId
        operator
        delegator
        epoch
        dynasty
        transactionHash
        timestamp
      }

      operatorRewardChanged {
        id
        operator
        reason
        prevClaimableAmount
        newClaimableAmount
        dynasty
        epoch
        transactionHash
      }

      metalayerRecoveryEvent {
        id
        activeSetBeingJailedSlashed
        epoch
        dynasty
        transactionHash
      }

      blameConsensusEvent {
        id
        blameType
        culprits
        epoch
        dynasty
        transactionHash
      }

      chainStatusChangedEvent {
        id
        chainDomain
        isSupported
        epoch
        dynasty
        sender
        timestamp
        transactionHash
      }
      signerProofDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
        transactionHash
      }

      validatorWithdrawalCredentialVerifiedEvent {
        id
        operatorId
        validatorIndex
        epoch
        dynasty
        transactionHash
        timestamp
        sender
      }
      operatorJailedEvent {
        jailEndDynasty
        sender
        transactionHash
        operatorId
        epoch
        dynasty
        id
      }
      blockDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
      }
      commissionUpdatedEvent {
        commission
        id
        operator
        operatorId
      }
      delegationAcceptanceChangedEvent {
        acceptDelegation
        id
        operator
        operatorId
      }
      escapeHatchEvent {
        amount
        dynasty
        epoch
        id
        sender
      }
      genesisResetTimestampSetEvent {
        genesisResetTimestamp
        id
        initModeTimePeriod
      }
      messageConsensusReachedEvent {
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        dynasty
        epoch
        id
        message
      }
      signerConsensusReachedEvent {
        signerAddress
        id
        epoch
        dynasty
      }
    }
  }
`;

export const FETCH_TRANSACTION_BY_ACTION = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $actions: [String]
    $skip: Int!
    $first: Int!
  ) {
    transactions(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: { action_in: $actions }
    ) {
      id
      timestamp
      transactionHash
      address
      dynasty
      epoch
      action
      attestedEvent {
        epoch
        dynasty
        operatorId
        sender
        status
        signerAddress
        transactionHash
      }
      confirmTransferEvent {
        id
        signature
        oldSignerAddress
        newSignerAddress
        epoch
        dynasty
        timestamp
        transactionHash
      }
      requestCommittedEvent {
        id
        epoch
        dynasty
        requestId
        sender
        timestamp
        transactionHash
        operatorId
        commitment
      }
      requestStatusChangedEvent {
        id
        requestId
        epoch
        dynasty
        status
        timestamp
        transactionHash
      }
      requestRevealedEvent {
        id
        epoch
        dynasty
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        timestamp
        transactionHash
        values
      }
      operatorSnitchedEvent {
        id
        requestId
        destinationChainDomain
        bountyHunter
        dynasty
        epoch
        requestingChainDomain
        target
        timestamp
        transactionHash
        operatorId
        values
      }
      createRequestEvent {
        dynasty
        requesterAddress
        numRequests
        timestamp
        epoch
        transactionHash
        sourceChainId
        sourceAddress
        payload
        targetChainId
      }
      finalizeBlockEvent {
        blockWinner
        signature
        sender
        signerAddress
        epoch
        dynasty
        messageData
        requests {
          id
          requestId
          indexedAt
          requestFinalizedEpoch
          status
          resultAggregationType
          resultRequestingChainDomain
          resultDestinationChainDomain
          requestFirstCommittedEpoch
          resultTarget
          resultValues
        }
        requestIds
        numRequests
        transactionHash
      }
      operatorSelectionEvent {
        operatorId
        activeSet
        iteration
        transactionHash
        timestamp
        isOperatorSelected
      }
      stakedEvent {
        amount
        dynasty
        epoch
        transactionHash
        operator
        operatorId
      }

      validatorsProvedEvent {
        id
        operatorId
        operatorStake
        timestamp
        transactionHash
        epoch
        dynasty
      }

      tooManyOperatorsSnitchedEvent {
        id
        activeSet
        dynasty
        epoch
        numOperatorsSuspended
        timestamp
        transactionHash
      }

      operatorsChurnedOutEvent {
        churnedOutOperators
        dynasty
        epoch
        id
        timestamp
        transactionHash
      }
      WithdrawalRequestedEvent {
        id
        epoch
        dynasty
        amount
        operator
        operatorId
        timestamp
        transactionHash
        unlockTime
      }
      WithdrawalCompletedEvent {
        id
        operator
        operatorId
        timestamp
        epoch
        transactionHash
        dynasty
        amount
      }
      withdrawEvent {
        operatorId
        operator
        transactionHash
        mode
        id
        epoch
        dynasty
      }
      parameterEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      parameterChangeScheduledEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      attestBlameEvent {
        blameType
        culprits
        dynasty
        epoch
        id
        sender
        transactionHash
      }
      slashedEvent {
        dynasty
        epoch
        id
        newStake
        prevStake
        sender
        operator
        operatorId
        transactionHash
      }
      stakeChangeEvent {
        dynasty
        epoch
        id
        newStake
        prevStake
        reason
        timestamp
        transactionHash
        operatorId
      }
    delegatorPoolChangeEvent {
      id
      operatorId
      prevDelegatorPool
      newDelegatorPool
      reason
      epoch
      dynasty
      transactionHash
      timestamp
  }

      delegatedEvent {
        id
        delegator
        operator
        operatorId
        amount
        prevTotalStake
        newTotalStake
        totalSupply
        epoch
        dynasty
        transactionHash
        timestamp
      }

      quitNetworkEvent {
        id
        operator
        operatorId
        toBeChurnedOutForUnstake
        epoch
        dynasty
        transactionHash
        timestamp
      }

      withdrawSignalledEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        transactionHash
        timestamp
      }

      withdrawInitiatedEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        newStake
        transactionHash
        timestamp
      }

      withdrawExecutedEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        newStake
        transactionHash
        timestamp
      }

      resetSignalLockEvent {
        id
        operatorId
        operator
        delegator
        epoch
        dynasty
        transactionHash
        timestamp
      }

      operatorRewardChanged {
        id
        operator
        reason
        prevClaimableAmount
        newClaimableAmount
        dynasty
        epoch
        transactionHash
      }

      metalayerRecoveryEvent {
        id
        activeSetBeingJailedSlashed
        epoch
        dynasty
        transactionHash
      }

      blameConsensusEvent {
        id
        blameType
        culprits
        epoch
        dynasty
        transactionHash
      }

      chainStatusChangedEvent {
        id
        chainDomain
        isSupported
        epoch
        dynasty
        sender
        timestamp
        transactionHash
      }
      signerProofDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
        transactionHash
      }

      validatorWithdrawalCredentialVerifiedEvent {
        id
        operatorId
        validatorIndex
        epoch
        dynasty
        transactionHash
        timestamp
        sender
      }
      operatorJailedEvent {
        jailEndDynasty
        sender
        transactionHash
        operatorId
        epoch
        dynasty
        id
      }
      blockDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
      }
      commissionUpdatedEvent {
        commission
        id
        operator
        operatorId
      }
      delegationAcceptanceChangedEvent {
        acceptDelegation
        id
        operator
        operatorId
      }
      escapeHatchEvent {
        amount
        dynasty
        epoch
        id
        sender
      }
      genesisResetTimestampSetEvent {
        genesisResetTimestamp
        id
        initModeTimePeriod
      }
      messageConsensusReachedEvent {
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        dynasty
        epoch
        id
        message
      }
      signerConsensusReachedEvent {
        signerAddress
        id
        epoch
        dynasty
      }
    }
  }
`;

export const FETCH_ATTESTED_SIGNERS_BY_HASH = gql`
  query($hash: String!) {
    attestedEvents(where: { transactionHash: $hash }) {
      epoch
      dynasty
      operatorId
      status
      sender
      signerAddress
      votes
      timestampWithVotes
      transactionHash
      timestamp
    }
  }
`;

export const FETCH_ATTESTED_SIGNERS_BY_ADDRESS = gql`
  query(
    $address: String!
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
  ) {
    attestedEvents(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: { sender: $address }
    ) {
      epoch
      dynasty
      operatorId
      status
      sender
      signerAddress
      votes
      timestampWithVotes
      transactionHash
      timestamp
    }
  }
`;

export const FETCH_FINALIZED_BLOCKS_BY_HASH = gql`
  query($hash: String!) {
    finalizeBlockEvents(where: { transactionHash: $hash }) {
      blockWinner
      signature
      sender
      signerAddress
      epoch
      dynasty
      messageData
      requests {
        id
        requestId
        indexedAt
        requestFinalizedEpoch
        status
        resultAggregationType
        resultRequestingChainDomain
        resultDestinationChainDomain
        requestFirstCommittedEpoch
        resultTarget
        resultValues
      }
      requestIds
      numRequests
      transactionHash
      timestamp
    }
  }
`;

export const FETCH_FINALIZED_BLOCKS_BY_EPOCH = gql`
  query($epoch: Int!) {
    finalizeBlockEvents(where: { epoch: $epoch }) {
      blockWinner
      signature
      sender
      signerAddress
      epoch
      dynasty
      messageData
      requests {
        id
        requestId
        indexedAt
        requestFinalizedEpoch
        status
        resultAggregationType
        resultRequestingChainDomain
        resultDestinationChainDomain
        requestFirstCommittedEpoch
        resultTarget
        resultValues
      }
      requestIds
      numRequests
      transactionHash
      timestamp
    }
  }
`;

export const FETCH_FINALIZED_BLOCKS_BY_ADDRESS = gql`
  query($address: String!, $orderBy: String!, $orderDirection: String!) {
    finalizeBlockEvents(
      orderBy: $orderBy
      orderDirection: $orderDirection
      where: { sender: $address }
    ) {
      blockWinner
      signature
      sender
      requests {
        id
        requestId
        indexedAt
        requestFinalizedEpoch
        status
        resultAggregationType
        resultRequestingChainDomain
        resultDestinationChainDomain
        requestFirstCommittedEpoch
        resultTarget
        resultValues
      }
      requestIds
      numRequests
      signerAddress
      epoch
      dynasty
      messageData
      transactionHash
      timestamp
    }
  }
`;

export const FETCH_SEARCH_BY_ADDRESS = gql`
  query($address: String!) {
    operators(where: { address: $address }) {
      address
    }
  }
`;

export const FETCH_TRANSACTION_BY_HASH_SEARCH = gql`
  query($hash: String!) {
    transactions(where: { transactionHash: $hash }) {
      timestamp
      transactionHash
      address
    }
  }
`;

export const FETCH_LAST_SYNC_BLOCK = gql`
  query {
    _meta {
      block {
        number
      }
    }
  }
`;

export const FETCH_TRANSACTIONS_BY_EPOCH = gql`
  query($epoch: String!) {
    transactions(where: { epoch: $epoch }) {
      id
      timestamp
      transactionHash
      address
      dynasty
      epoch
      action
      attestedEvent {
        epoch
        dynasty
        operatorId
        sender
        status
        signerAddress
        transactionHash
      }
      confirmTransferEvent {
        id
        signature
        oldSignerAddress
        newSignerAddress
        epoch
        dynasty
        timestamp
        transactionHash
      }
      requestCommittedEvent {
        id
        epoch
        dynasty
        requestId
        sender
        timestamp
        transactionHash
        operatorId
        commitment
      }
      requestStatusChangedEvent {
        id
        requestId
        epoch
        dynasty
        status
        timestamp
        transactionHash
      }
      requestRevealedEvent {
        id
        epoch
        dynasty
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        timestamp
        transactionHash
        values
      }
      operatorSnitchedEvent {
        id
        requestId
        destinationChainDomain
        bountyHunter
        dynasty
        epoch
        requestingChainDomain
        target
        timestamp
        transactionHash
        operatorId
        values
      }
      createRequestEvent {
        dynasty
        requesterAddress
        numRequests
        timestamp
        epoch
        transactionHash
        sourceChainId
        sourceAddress
        payload
        targetChainId
      }
      finalizeBlockEvent {
        blockWinner
        signature
        sender
        signerAddress
        epoch
        dynasty
        messageData
        requests {
          id
          requestId
          indexedAt
          requestFinalizedEpoch
          status
          resultAggregationType
          resultRequestingChainDomain
          resultDestinationChainDomain
          requestFirstCommittedEpoch
          resultTarget
          resultValues
        }
        requestIds
        numRequests
        transactionHash
      }
      operatorSelectionEvent {
        operatorId
        activeSet
        iteration
        transactionHash
        timestamp
        isOperatorSelected
      }
      stakedEvent {
        amount
        dynasty
        epoch
        transactionHash
        operator
        operatorId
      }

      validatorsProvedEvent {
        id
        operatorId
        operatorStake
        timestamp
        transactionHash
        epoch
        dynasty
      }

      tooManyOperatorsSnitchedEvent {
        id
        activeSet
        dynasty
        epoch
        numOperatorsSuspended
        timestamp
        transactionHash
      }

      operatorsChurnedOutEvent {
        churnedOutOperators
        dynasty
        epoch
        id
        timestamp
        transactionHash
      }
      WithdrawalRequestedEvent {
        id
        epoch
        dynasty
        amount
        operator
        operatorId
        timestamp
        transactionHash
        unlockTime
      }
      WithdrawalCompletedEvent {
        id
        operator
        operatorId
        timestamp
        epoch
        transactionHash
        dynasty
        amount
      }
      withdrawEvent {
        operatorId
        operator
        transactionHash
        mode
        id
        epoch
        dynasty
      }
      parameterEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      parameterChangeScheduledEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      attestBlameEvent {
        blameType
        culprits
        dynasty
        epoch
        id
        sender
        transactionHash
      }
      slashedEvent {
        dynasty
        epoch
        id
        newStake
        prevStake
        sender
        operator
        operatorId
        transactionHash
      }
      stakeChangeEvent {
        dynasty
        epoch
        id
        newStake
        prevStake
        reason
        timestamp
        transactionHash
        operatorId
      }
    delegatorPoolChangeEvent {
      id
      operatorId
      prevDelegatorPool
      newDelegatorPool
      reason
      epoch
      dynasty
      transactionHash
      timestamp
  }

      delegatedEvent {
        id
        delegator
        operator
        operatorId
        amount
        prevTotalStake
        newTotalStake
        totalSupply
        epoch
        dynasty
        transactionHash
        timestamp
      }

      quitNetworkEvent {
        id
        operator
        operatorId
        toBeChurnedOutForUnstake
        epoch
        dynasty
        transactionHash
        timestamp
      }

      withdrawSignalledEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        transactionHash
        timestamp
      }

      withdrawInitiatedEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        newStake
        transactionHash
        timestamp
      }

      withdrawExecutedEvent {
        id
        withdrawer
        operator
        operatorId
        epoch
        dynasty
        amount
        newStake
        transactionHash
        timestamp
      }

      resetSignalLockEvent {
        id
        operatorId
        operator
        delegator
        epoch
        dynasty
        transactionHash
        timestamp
      }

      operatorRewardChanged {
        id
        operator
        reason
        prevClaimableAmount
        newClaimableAmount
        dynasty
        epoch
        transactionHash
      }

      metalayerRecoveryEvent {
        id
        activeSetBeingJailedSlashed
        epoch
        dynasty
        transactionHash
      }

      blameConsensusEvent {
        id
        blameType
        culprits
        epoch
        dynasty
        transactionHash
      }

      chainStatusChangedEvent {
        id
        chainDomain
        isSupported
        epoch
        dynasty
        sender
        timestamp
        transactionHash
      }
      signerProofDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
        transactionHash
      }

      validatorWithdrawalCredentialVerifiedEvent {
        id
        operatorId
        validatorIndex
        epoch
        dynasty
        transactionHash
        timestamp
        sender
      }
      operatorJailedEvent {
        jailEndDynasty
        sender
        transactionHash
        operatorId
        epoch
        dynasty
        id
      }
      blockDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
      }
      commissionUpdatedEvent {
        commission
        id
        operator
        operatorId
      }
      delegationAcceptanceChangedEvent {
        acceptDelegation
        id
        operator
        operatorId
      }
      escapeHatchEvent {
        amount
        dynasty
        epoch
        id
        sender
      }
      genesisResetTimestampSetEvent {
        genesisResetTimestamp
        id
        initModeTimePeriod
      }
      messageConsensusReachedEvent {
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        dynasty
        epoch
        id
        message
      }
      signerConsensusReachedEvent {
        signerAddress
        id
        epoch
        dynasty
      }
    }
  }
`;

export const FETCH_TRANSACTIONS_BY_DYNASTY = gql`
  query($dynasty: String!, $orderBy: String!, $orderDirection: String!) {
    transactions(
      orderBy: $orderBy
      orderDirection: $orderDirection
      where: { dynasty: $dynasty }
    ) {
      id
      timestamp
      transactionHash
      address
      dynasty
      epoch
      action
      attestedEvent {
        epoch
        dynasty
        operatorId
        sender
        status
        signerAddress
        transactionHash
      }
      confirmTransferEvent {
        id
        signature
        oldSignerAddress
        newSignerAddress
        epoch
        dynasty
        timestamp
        transactionHash
      }
      requestCommittedEvent {
        id
        epoch
        dynasty
        requestId
        sender
        timestamp
        transactionHash
        operatorId
        commitment
      }
      requestStatusChangedEvent {
        id
        requestId
        epoch
        dynasty
        status
        timestamp
        transactionHash
      }
      requestRevealedEvent {
        id
        epoch
        dynasty
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        timestamp
        transactionHash
        values
      }
      operatorSnitchedEvent {
        id
        requestId
        destinationChainDomain
        bountyHunter
        dynasty
        epoch
        requestingChainDomain
        target
        timestamp
        transactionHash
        operatorId
        values
      }
      createRequestEvent {
        dynasty
        requesterAddress
        numRequests
        timestamp
        epoch
        transactionHash
        sourceChainId
        sourceAddress
        payload
        targetChainId
      }
      finalizeBlockEvent {
        blockWinner
        signature
        sender
        signerAddress
        epoch
        dynasty
        messageData
        requests {
          id
          requestId
          indexedAt
          requestFinalizedEpoch
          status
          resultAggregationType
          resultRequestingChainDomain
          resultDestinationChainDomain
          requestFirstCommittedEpoch
          resultTarget
          resultValues
        }
        requestIds
        numRequests
        transactionHash
      }
      operatorSelectionEvent {
        operatorId
        activeSet
        iteration
        transactionHash
        timestamp
        isOperatorSelected
      }
      stakedEvent {
        amount
        dynasty
        epoch
        transactionHash
        operator
        operatorId
      }

      validatorsProvedEvent {
        id
        operatorId
        operatorStake
        timestamp
        transactionHash
        epoch
        dynasty
      }

      tooManyOperatorsSnitchedEvent {
        id
        activeSet
        dynasty
        epoch
        numOperatorsSuspended
        timestamp
        transactionHash
      }

      operatorsChurnedOutEvent {
        churnedOutOperators
        dynasty
        epoch
        id
        timestamp
        transactionHash
      }
      WithdrawalRequestedEvent {
        id
        epoch
        dynasty
        amount
        operator
        operatorId
        timestamp
        transactionHash
        unlockTime
      }
      WithdrawalCompletedEvent {
        id
        operator
        operatorId
        timestamp
        epoch
        transactionHash
        dynasty
        amount
      }
      withdrawEvent {
        operatorId
        operator
        transactionHash
        mode
        id
        epoch
        dynasty
      }
      parameterEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      parameterChangeScheduledEvent {
        id
        admin
        parameterName
        valueChangedTo
        epoch
        dynasty
        timestamp
        transactionHash
      }
      attestBlameEvent {
        blameType
        culprits
        dynasty
        epoch
        id
        sender
        transactionHash
      }

      delegatedEvent {
        id
        delegator
        operator
        operatorId
        amount
        prevTotalStake
        newTotalStake
        totalSupply
        epoch
        dynasty
        transactionHash
        timestamp
      }

      operatorRewardChanged {
        id
        operator
        reason
        prevClaimableAmount
        newClaimableAmount
        dynasty
        epoch
        transactionHash
      }

      metalayerRecoveryEvent {
        id
        activeSetBeingJailedSlashed
        epoch
        dynasty
        transactionHash
      }

      blameConsensusEvent {
        id
        blameType
        culprits
        epoch
        dynasty
        transactionHash
      }

      chainStatusChangedEvent {
        id
        chainDomain
        isSupported
        epoch
        dynasty
        sender
        timestamp
        transactionHash
      }
      signerProofDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
        transactionHash
      }

      validatorWithdrawalCredentialVerifiedEvent {
        id
        operatorId
        validatorIndex
        epoch
        dynasty
        transactionHash
        timestamp
        sender
      }
      operatorJailedEvent {
        jailEndDynasty
        sender
        transactionHash
        operatorId
        epoch
        dynasty
        id
      }
      blockDisputeReachedEvent {
        activeSet
        dynasty
        epoch
        id
        reason
        sender
      }
      commissionUpdatedEvent {
        commission
        id
        operator
        operatorId
      }
      delegationAcceptanceChangedEvent {
        acceptDelegation
        id
        operator
        operatorId
      }
      escapeHatchEvent {
        amount
        dynasty
        epoch
        id
        sender
      }
      genesisResetTimestampSetEvent {
        genesisResetTimestamp
        id
        initModeTimePeriod
      }
      messageConsensusReachedEvent {
        requestId
        requestingChainDomain
        destinationChainDomain
        target
        dynasty
        epoch
        id
        message
      }
      signerConsensusReachedEvent {
        signerAddress
        id
        epoch
        dynasty
      }
    }
  }
`;

export const FETCH_REQUESTS_BY_REQUESTING_CHAIN = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
    $requestingChainDomain: String
  ) {
    requests(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: { resultRequestingChainDomain: $requestingChainDomain }
    ) {
      id
      requestId
      numCommits
      indexedAt
      requestFinalizedEpoch
      requestFirstCommittedEpoch
      resultDestinationChainDomain
      resultRequestingChainDomain
      resultTarget
      resultValues
      status
      epochConsensusReached
    }
  }
`;

export const FETCH_REQUESTS_BY_DESTINATION_CHAIN = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
    $destinationChainDomain: String
  ) {
    requests(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: { resultDestinationChainDomain: $destinationChainDomain }
    ) {
      id
      requestId
      numCommits
      indexedAt
      requestFinalizedEpoch
      requestFirstCommittedEpoch
      resultDestinationChainDomain
      resultRequestingChainDomain
      resultTarget
      resultValues
      status
      epochConsensusReached
    }
  }
`;

export const FETCH_REQUESTS_BY_STATUS_AND_REQUESTING_CHAIN = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
    $requestingChainDomain: String!
    $status: [String]!
  ) {
    requests(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: {
        resultRequestingChainDomain: $requestingChainDomain
        status_in: $status
      }
    ) {
      id
      requestId
      numCommits
      indexedAt
      requestFinalizedEpoch
      requestFirstCommittedEpoch
      resultDestinationChainDomain
      resultRequestingChainDomain
      resultTarget
      resultValues
      status
      epochConsensusReached
    }
  }
`;

export const FETCH_REQUESTS_BY_STATUS_AND_DESTINATION_CHAIN = gql`
  query(
    $orderBy: String!
    $orderDirection: String!
    $skip: Int!
    $first: Int!
    $destinationChainDomain: String!
    $status: [String]!
  ) {
    requests(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: {
        resultDestinationChainDomain: $destinationChainDomain
        status_in: $status
      }
    ) {
      id
      requestId
      numCommits
      indexedAt
      requestFinalizedEpoch
      requestFirstCommittedEpoch
      resultDestinationChainDomain
      resultRequestingChainDomain
      resultTarget
      resultValues
      status
      epochConsensusReached
    }
  }
`;

export const FETCH_ACTIVE_SET_OPERATORS = gql`
  query($orderBy: String!, $orderDirection: String!, $isChurnedOut: Boolean!) {
    operators(
      orderBy: $orderBy
      orderDirection: $orderDirection
      skip: $skip
      first: $first
      where: { isChurnedOut: $isChurnedOut }
    ) {
      stake
      operatorId
      apy
      lastActiveTimestamp
      lastAttestedEpoch
      lastAttestedDynasty
      lastFinalizedBlock
      lastEpochChurnedOut
      lastDynastyChurnedOut
      isChurnedOut
      address
      delegationAccepted
      dToken
    }
  }
`;
